import type { AdvisorDraft, AdvisorDraftDay } from '../model/types';
import { DayBucket } from '@itineraries/widgets/itinerary-days/ui/day-bucket';
import { formatPrice } from '@products/entities/product/lib/price';
import { useState } from 'react';
import { joinCss } from '@/shared/lib/css-utils';
import { Button } from '@/shared/ui/button';
import { Heading } from '@/shared/ui/heading';
import { Modal, ModalFooter, ModalHeader } from '@/shared/ui/modal';
import { Text } from '@/shared/ui/text';
import { draftDayCount, draftItemCount } from '../model/add-item';
import { ADVISOR_RAIL_DROP_ID, advisorDayDropId } from '../model/dnd';
import { draftHasSuggested } from '../model/merge-generate';
import { estimateAgencyEarn, sumClientEstimate } from '../model/price-estimate';
import { titleCaseRegionTitle } from '../model/region-layout';
import { AdvisorDayItem } from './advisor-day-item';
import css from './advisor-planner-workspace.module.scss';

type ItineraryRailProps = {
  draft: AdvisorDraft;
  generateError: boolean;
  isGenerating: boolean;
  generatingDayId: string | null;
  onGenerate: () => void;
  onGenerateDay: (legId: string, dayId: string) => void;
  onRemoveSuggested: (legId: string | null) => void;
  onAddDay: (legId: string) => void;
  onRemoveLeg: (legId: string) => void;
  onRemoveItem: (itemId: string) => void;
};

export const ItineraryRail = ({
  draft,
  generateError,
  isGenerating,
  generatingDayId,
  onGenerate,
  onGenerateDay,
  onRemoveSuggested,
  onAddDay,
  onRemoveLeg,
  onRemoveItem,
}: ItineraryRailProps) => {
  const items = draftItemCount(draft);
  const days = draftDayCount(draft);
  const clientEstimate = sumClientEstimate(
    draft.legs.flatMap((leg) =>
      leg.days.flatMap((day) => day.items.map((item) => item.clientPrice))
    )
  );
  const earn = estimateAgencyEarn(clientEstimate);
  const currency = draft.legs[0]?.days[0]?.items[0]?.currency ?? 'EUR';
  const [pendingCity, setPendingCity] = useState<{
    id: string;
    title: string;
  } | null>(null);

  return (
    <aside className={css.itineraryPane} aria-label="Itinerary">
      <header className={css.itineraryHeader}>
        <div className={css.topBar}>
          <Heading as="h2" size="md">
            Itinerary
          </Heading>
          <div className={css.railActions}>
            {draftHasSuggested(draft) ? (
              <Button
                onClick={() => onRemoveSuggested(null)}
                size="sm"
                type="button"
                variant="link"
              >
                Remove suggested
              </Button>
            ) : null}
            {days === 0 ? (
              <Button
                disabled={isGenerating}
                onClick={onGenerate}
                size="sm"
                type="button"
                variant="dark"
              >
                {isGenerating ? 'Generating…' : 'Generate'}
              </Button>
            ) : null}
          </div>
        </div>
        <Text size="xs" variant="muted">
          {draft.legs.length} region{draft.legs.length === 1 ? '' : 's'} ·{' '}
          {days} day{days === 1 ? '' : 's'}
        </Text>
        {generateError ? (
          <Text size="xs" variant="muted">
            Could not generate draft
          </Text>
        ) : null}
      </header>
      <div className={css.legList}>
        {draft.legs.length === 0 && <EmptyRailDrop />}
        {draft.legs.map((leg) => (
          <section className={css.leg} key={leg.id}>
            <header className={css.legHeader}>
              <Heading as="h3" className={css.legTitle} size="sm">
                {titleCaseRegionTitle(leg.regionTitle)}
              </Heading>
              <div className={css.railActions}>
                <Button
                  onClick={() =>
                    setPendingCity({
                      id: leg.id,
                      title: titleCaseRegionTitle(leg.regionTitle),
                    })
                  }
                  size="sm"
                  type="button"
                  variant="link"
                >
                  Remove city
                </Button>
              </div>
            </header>
            {leg.days.map((day, index) => (
              <DayDrop
                day={day}
                index={index}
                isBusy={isGenerating && generatingDayId === day.id}
                isGenerating={isGenerating}
                key={day.id}
                onGenerate={() => onGenerateDay(leg.id, day.id)}
                onRemoveItem={onRemoveItem}
              />
            ))}
            <Button
              onClick={() => onAddDay(leg.id)}
              size="sm"
              type="button"
              variant="link"
            >
              + Add day
            </Button>
          </section>
        ))}
      </div>
      <footer className={css.budgetDock}>
        <div className={css.budgetRow}>
          <Text size="sm">Items</Text>
          <Text size="sm" weight="semibold">
            {items}
          </Text>
        </div>
        <div className={css.budgetRow}>
          <Text size="sm">Client estimate</Text>
          <Text size="sm" weight="semibold">
            {formatPrice(clientEstimate, currency)}
          </Text>
        </div>
        <div className={css.budgetRow}>
          <Text size="sm">You earn</Text>
          <Text className={css.budgetEarn} size="sm" weight="semibold">
            {formatPrice(earn, currency)}
          </Text>
        </div>
      </footer>
      <Modal onClose={() => setPendingCity(null)} open={pendingCity !== null}>
        <ModalHeader onClose={() => setPendingCity(null)} title="Remove city" />
        <div className={css.confirmBody}>
          <Text>
            Remove <b>{pendingCity?.title}</b> and all its days from this
            itinerary?
          </Text>
        </div>
        <ModalFooter>
          <Button
            onClick={() => setPendingCity(null)}
            type="button"
            variant="ghost"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!pendingCity) return;
              onRemoveLeg(pendingCity.id);
              setPendingCity(null);
            }}
            type="button"
            variant="destructive"
          >
            Remove city
          </Button>
        </ModalFooter>
      </Modal>
    </aside>
  );
};

const EmptyRailDrop = () => (
  <DayBucket
    dayIndex={-1}
    droppableId={ADVISOR_RAIL_DROP_ID}
    ignoreCollapsed
    isEmpty
    itemIds={[]}
  >
    {null}
  </DayBucket>
);

const DayDrop = ({
  day,
  index,
  isBusy,
  isGenerating,
  onGenerate,
  onRemoveItem,
}: {
  day: AdvisorDraftDay;
  index: number;
  isBusy: boolean;
  isGenerating: boolean;
  onGenerate: () => void;
  onRemoveItem: (itemId: string) => void;
}) => (
  <div
    aria-busy={isBusy}
    className={joinCss(css.dayBlock, isBusy ? css.dayBlockBusy : undefined)}
  >
    <div className={css.dayHeader}>
      <Text size="sm" weight="semibold">
        Day {index + 1}
      </Text>
      <Button
        aria-label={
          isBusy ? `Generating day ${index + 1}` : `Generate day ${index + 1}`
        }
        disabled={isGenerating}
        onClick={onGenerate}
        size="sm"
        type="button"
        variant="dark"
      >
        {isBusy ? 'Generating…' : 'Generate'}
      </Button>
    </div>
    {isBusy ? (
      <Text size="xs" variant="muted">
        Filling this day…
      </Text>
    ) : null}
    <DayBucket
      dayIndex={index}
      droppableData={{ kind: 'advisor-day', dayId: day.id }}
      droppableId={advisorDayDropId(day.id)}
      ignoreCollapsed
      isEmpty={day.items.length === 0}
      itemIds={day.items.map((item) => item.id)}
    >
      <div className={css.dayItems}>
        {day.items.map((item) => (
          <AdvisorDayItem
            item={item}
            key={item.id}
            onRemove={() => onRemoveItem(item.id)}
          />
        ))}
      </div>
    </DayBucket>
  </div>
);
